import { Alert, Button, Card, Col, Collapse, Empty, Form, Input, Row, Space, Table, Typography } from 'antd';
import { ValidatorRule } from 'rc-field-form/lib/interface';
import { FC, ReactElement } from 'react';
import { CaretRightFill, DashCircle, ExclamationCircleFill, PlusCircle } from 'react-bootstrap-icons';

import { ButtonSuccess } from '@/common/components';
import { AppFormProps } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import { HostingsAccountsAutocomplete, HostingsAllAutocomplete } from '@/modules/Hostings';
import { ProxyServerFormFields, ProxyServerFormInputs, SupportedLinuxOS } from '@/modules/ProxyServers';

interface Props extends AppFormProps<typeof ProxyServerFormInputs, ProxyServerFormFields> {
  hostingIdFormValue: string | undefined;
  supportedOs?: SupportedLinuxOS[]
  existingIpsCount?: number;
}

export const ProxyServersFormView: FC<Props> = ({
  formName,
  formType,
  form,
  initialValues,
  isFormLoading,
  formErrors,
  validationSchema,
  hostingIdFormValue,
  supportedOs,
  existingIpsCount = 0,
  handleBack,
  handleFinish
}) => {

  return (
    <Form
      form={ form }
      name={ formName }
      layout="vertical"
      initialValues={ initialValues }
      disabled={ isFormLoading }
      autoComplete="off"
      onFinish={ handleFinish }
    >
      { formType !== 'edit' && (
        <Form.Item>
          <Alert
            showIcon
            icon={ <ExclamationCircleFill/> }
            style={{ padding: '12px 8px' }}
            type="info"
            message="ВАЖЛИВО!"
            description={
              <Collapse
                ghost size="small"
                style={{ padding: 0, marginLeft: -16 }}
                expandIcon={ (properties) => (
                  <CaretRightFill
                    size={ 16 }
                    style={{ transition: '300ms', transform: `rotate(${ properties.isActive ? 90 : 0 }deg)` }}
                  />
                ) }
              >
                <Collapse.Panel
                  key="1"
                  header="Доступні OS серверів для налаштування:"
                  style={{ padding: 0 }}
                >
                  <Table
                    className="proxy-servers-supported-os-table"
                    pagination={ false }
                    size="small"
                    dataSource={
                      (supportedOs ?? []).map((elem) => ({
                        key: `${ elem.id }:${ elem.version }`,
                        id: elem.id,
                        version: elem.version,
                        codename: elem.codename
                      }))
                    }
                    columns={ [
                      { title: 'ID', dataIndex: 'id', key: 'id' },
                      { title: 'Версія', dataIndex: 'version', key: 'version' },
                      {
                        title: <>
                          Кодова назва <br/>
                          <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: 1 }}>
                            (за наявності)
                          </Typography.Text>
                        </>,
                        dataIndex: 'codename',
                        key: 'codename'
                      }
                    ] }
                  />
                </Collapse.Panel>
              </Collapse>
            }
          />
        </Form.Item>
      ) }

      <Form.Item
        label="Назва"
        required
        name={ ProxyServerFormInputs.Name }
        hasFeedback
        help={ formErrors?.name }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.name) }
        rules={ validationSchema.name }
      >
        <Input allowClear/>
      </Form.Item>

      <Form.Item
        label="Хостинг"
        name={ ProxyServerFormInputs.HostingId }
        required
        hasFeedback
        help={ formErrors?.hostingId }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.hostingId) }
        rules={ validationSchema.hostingId }
      >
        <HostingsAllAutocomplete/>
      </Form.Item>

      <Form.Item
        label="Акаунт хостинга"
        required
        name={ ProxyServerFormInputs.HostingAccountId }
        hasFeedback
        help={ formErrors?.hostingAccountId }
        validateStatus={ defineInputStatus(isFormLoading) }
        rules={ validationSchema.hostingAccountId }
      >
        <HostingsAccountsAutocomplete hostingId={ hostingIdFormValue }/>
      </Form.Item>

      <Form.Item
        label="Пароль від сервера"
        required={ formType !== 'edit' }
        name={ ProxyServerFormInputs.Password }
        hasFeedback
        help={ formErrors?.password || (formType === 'edit' ? 'Залиште порожнім, щоб не змінювати пароль' : undefined) }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.password) }
        rules={ validationSchema.password }
      >
        <Input.Password allowClear/>
      </Form.Item>

      <Form.List name={ ProxyServerFormInputs.Ips } rules={ validationSchema.ips as ValidatorRule[] }>
        { (fields, { add, remove }, { errors }): ReactElement => (
          <Row style={{ marginBottom: 24 }}>
            <Col span={ 24 }>
              <Card
                title={ formType === 'edit' ? 'Додати нові IP адреси' : 'Список IP адрес' }
                styles={{ body: { marginBottom: -24 } }}
                extra={ [
                  <Button
                    key="add"
                    disabled={ isFormLoading }
                    type="dashed"
                    onClick={ (): void => add() }
                    icon={ <PlusCircle/> }
                  >
                    Додати IP адресу
                  </Button>
                ] }
              >
                { fields.length ? (
                  fields.map((field) => {
                    const isExistingIp = field.name < existingIpsCount;

                    return (
                    <Form.Item
                      key={ field.key }
                      required={ false }
                      wrapperCol={{ span: 24 }}
                      validateTrigger={ ['onChange', 'onBlur'] }
                      validateStatus={ defineInputStatus(
                        isFormLoading,
                        form.getFieldError([ProxyServerFormInputs.Ips, field.name])[0] ||
                          formErrors?.[`${ ProxyServerFormInputs.Ip }[${ field.name }]`]
                      ) }
                      help={
                        form.getFieldError([ProxyServerFormInputs.Ips, field.name])[0] ||
                        formErrors?.[`${ ProxyServerFormInputs.Ip }[${ field.name }]`]
                      }
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 8 }}>
                        <Form.Item
                          { ...field }
                          name={ field.name }
                          rules={ isExistingIp ? [] : validationSchema.ip }
                          noStyle
                          required={ !isExistingIp }
                          validateTrigger={ ['onChange', 'onBlur'] }
                          validateStatus={ defineInputStatus(
                            isFormLoading,
                            formErrors?.[`${ ProxyServerFormInputs.Ip }[${ field.name }]`]
                          ) }
                        >
                          <Input placeholder="0.0.0.0" allowClear={ !isExistingIp } disabled={ isFormLoading || isExistingIp }/>
                        </Form.Item>
                        { !isExistingIp && (
                          <Button danger type="text" onClick={ (): void => remove(field.name) } icon={ <DashCircle/> }/>
                        ) }
                      </div>
                    </Form.Item>
                    );
                  })
                ) : (
                  <Empty
                    image={ Empty.PRESENTED_IMAGE_SIMPLE }
                    description={ formType === 'edit' ? 'Немає нових IP адрес для додавання' : 'Додайте IP адресу' }
                  />
                ) }
              </Card>
            </Col>
            <Col span={ 24 } style={{ height: errors.length || formErrors?.ips ? 22 : 0 }}>
              <Form.Item>
                <Form.ErrorList errors={ formErrors?.ips ? [formErrors.ips] : errors }/>
              </Form.Item>
            </Col>
          </Row>
        ) }
      </Form.List>

      <Form.Item wrapperCol={{ span: 24 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button type="default" onClick={ handleBack }>
            Скасувати
          </Button>
          <ButtonSuccess loading={ isFormLoading } type="primary" htmlType="submit">
            { formType === 'edit' ? 'Зберегти' : 'Створити' }
          </ButtonSuccess>
        </Space>
      </Form.Item>
    </Form>
  );
};
